import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkoutPreviewPanel = ({ selectedWorkout, onClose, onQuickAssign }) => {
  if (!selectedWorkout) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 sticky top-24 h-[calc(100vh-120px)] flex items-center justify-center">
        <div className="text-center">
          <Icon name="Eye" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">Select a workout to preview</p>
        </div>
      </div>
    );
  }

  const { type, data } = selectedWorkout;
  const isExercise = type === 'exercise';

  const difficultyColors = {
    beginner: 'text-green-600 bg-green-100',
    intermediate: 'text-yellow-600 bg-yellow-100',
    advanced: 'text-red-600 bg-red-100'
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className="bg-card border border-border rounded-xl p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Preview</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Video/Image Preview */}
        {isExercise && data?.videoUrl && (
          <div className="mb-4 rounded-lg overflow-hidden bg-muted aspect-video flex items-center justify-center">
            <Icon name="Play" size={48} className="text-muted-foreground" />
          </div>
        )}

        {/* Title and Difficulty */}
        <div className="mb-4">
          <h4 className="text-xl font-bold text-foreground mb-2">{data?.name}</h4>
          <span className={`text-xs px-3 py-1 rounded-full ${difficultyColors?.[data?.difficulty]}`}>
            {data?.difficulty?.charAt(0)?.toUpperCase() + data?.difficulty?.slice(1)}
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4">{data?.description}</p>

        {/* Exercise-specific details */}
        {isExercise && (
          <div className="space-y-4 mb-6">
            {/* Category */}
            <div className="flex items-center gap-2">
              <Icon name="Tag" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">
                {data?.category?.charAt(0)?.toUpperCase() + data?.category?.slice(1)}
              </span>
            </div>

            {/* Muscle Groups */}
            {data?.muscleGroups && data?.muscleGroups?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Target Muscles</p>
                <div className="flex flex-wrap gap-1">
                  {data?.muscleGroups?.map((muscle, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-muted rounded-full text-foreground">
                      {muscle}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment */}
            {data?.equipmentNeeded && data?.equipmentNeeded?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Equipment Needed</p>
                <div className="flex flex-wrap gap-1">
                  {data?.equipmentNeeded?.map((equipment, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-muted rounded-full text-foreground">
                      {equipment}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Calories */}
            {data?.caloriesPerMinute && (
              <div className="flex items-center gap-2">
                <Icon name="Flame" size={16} className="text-orange-500" />
                <span className="text-sm text-foreground">
                  {data?.caloriesPerMinute} cal/min
                </span>
              </div>
            )}

            {/* Instructions */}
            {data?.instructions && data?.instructions?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Instructions</p>
                <ol className="space-y-2">
                  {data?.instructions?.map((instruction, idx) => (
                    <li key={idx} className="text-sm text-foreground flex gap-2">
                      <span className="text-primary font-medium">{idx + 1}.</span>
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Workout Plan-specific details */}
        {!isExercise && (
          <div className="space-y-4 mb-6">
            {/* Duration */}
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">{data?.durationMinutes} minutes</span>
            </div>

            {/* Tags */}
            {data?.tags && data?.tags?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Goals</p>
                <div className="flex flex-wrap gap-1">
                  {data?.tags?.map((tag, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                      {tag?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Exercise List */}
            {data?.workoutPlanExercises && data?.workoutPlanExercises?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Exercises ({data?.workoutPlanExercises?.length})
                </p>
                <div className="space-y-2">
                  {data?.workoutPlanExercises?.map((ex, idx) => (
                    <div key={idx} className="p-2 bg-muted rounded-lg">
                      <div className="flex items-start justify-between">
                        <span className="text-sm font-medium text-foreground">
                          {idx + 1}. {ex?.exercise?.name}
                        </span>
                      </div>
                      {(ex?.sets || ex?.reps) && (
                        <div className="flex items-center gap-3 mt-1">
                          {ex?.sets && (
                            <span className="text-xs text-muted-foreground">
                              {ex?.sets} sets
                            </span>
                          )}
                          {ex?.reps && (
                            <span className="text-xs text-muted-foreground">
                              {ex?.reps} reps
                            </span>
                          )}
                          {ex?.restSeconds && (
                            <span className="text-xs text-muted-foreground">
                              {ex?.restSeconds}s rest
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Assign Button */}
        {onQuickAssign && !isExercise && (
          <Button 
            onClick={() => onQuickAssign(data)} 
            fullWidth
            className="mt-4"
          >
            <Icon name="UserPlus" size={18} className="mr-2" />
            Quick Assign to Client
          </Button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default WorkoutPreviewPanel;